import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import * as cloneDeep from 'lodash/cloneDeep';
import * as moment from 'moment';

import { PurchaseOrderManagerService } from '../../../../../services/purchase-order/purchase-order-manager.service';
import { PurchaseOrderPDFComponent } from '../../../purchase-order-pdf/purchase-order-pdf.component';
import { CaptureSignatureComponent } from '../../../capture-signature/capture-signature.component';
import { ThemeManager } from '../../../../../managers/theme.manager';
import { PurchaseOrderPaymentType } from '../../../../../services/purchase-order/purchase-order.model';
import { Contact } from '../../../../../services/vendor/contact.model';
import { MessageService } from 'primeng/api';
import { LocationManagerService } from '../../../../../services/location/location-manager.service';
import { CurrencyConvertPipe } from 'src/app/services/custom-currency.pipe';
import { PaymentTypesSelectionComponent } from '../../payment-types-selection/payment-types-selection.component';
import { UiUtils } from 'src/app/shared-ui/utils/ui-utils';
import { AuthManagerService } from 'src/app/services/auth/auth-manager.service';
import { VehicleInfoComponent } from '../../vehicle-info/vehicle-info/vehicle-info.component';
import { lastValueFrom } from 'rxjs';
import { LocalCacheManager } from 'src/app/managers/local-cache.manager';
import { AnyARecord } from 'dns';

@Component({
    selector: 'ls-default-compliance-page',
    templateUrl: './default-compliance-page.component.html',
    styleUrls: [ './default-compliance-page.component.less' ]
})
export class DefaultCompliancePageComponent implements OnInit
{
    @ViewChild('signature', { static: false }) signature: CaptureSignatureComponent;

    @Input() isAdminEdit: boolean = false;
    @Output() signatureCompleted = new EventEmitter<boolean>();
    
    userCurrency: string = LocalCacheManager.currency;
    timeZones = UiUtils.timeZones;
    selectedTimezone: any = null;
    contactValid: boolean;
    idNum = '';
    idState = '';
    selectedPaymentTypes: PurchaseOrderPaymentType[] = [];
    selectedContact: Contact;

    headerText = 'Purchase Agreement';

    introRegularText1 = 'IN CONSIDERATION OF THE COVENANTS and agreements contained in this Purchase Agreement, the';
    introRegularText2 = 'parties to this Agreement agree as follows:';
    partiesText1 = ' (the "Seller"), of';
    partiesText2 = 'does hereby sell, assign and transfer to';
    partiesText3: string;
    partiesText4 = 'the following property...';
    paymentTypeHeaderText = 'Payment Type:';
    sellerInfoHeaderText = 'Seller information:';
    vehicleNumberText = 'Vehicle Number # __________________________________________';
    idText = 'ID or DL # _______________________________________________';
    stateText = 'State _______________';
    signaturePrelude1 = 'I, the undersigned, affirm under penalty of law that the property that is subject to this transaction is not';
    signaturePrelude2 = 'to the best of my knowledge stolen property. I certify that I am the legal owner of all merchandise sold.';
    signaturePrelude3 = 'The undersigned also affirms that they are authorized to receive payment on behalf of the above listed commercial enterprise.';
    signatureText = 'Signature ______________________________________________________';
    printText = 'Print _________________________________________________________';
    dateText = 'Date ___________________________';

    columns = [
        { title: 'PO #', widthPercent: 15, rowspan: 2 },
        { title: 'Description', widthPercent: 15 },
        { title: 'Quantity', widthPercent: 10 },
        { title: 'Purchase Price', widthPercent: 25 },
        { title: 'Sum', widthPercent: 15, rowspan: 2},
        { title: 'Adjustment', widthPercent: 15},
        { title: 'Total', widthPercent: 15, rowspan: 2}
    ];

    ThemeManager = ThemeManager;

    constructor(public poManager: PurchaseOrderManagerService,
                private locationManager: LocationManagerService,
                private messageService: MessageService,
                private currencyPipe: CurrencyConvertPipe,
                private authManager: AuthManagerService)
    { }

    ngOnInit(): void
    {
        this.clearSignatures();
        this.selectedTimezone = UiUtils.timeZones.find(x => x.fullName.toLocaleUpperCase() == this.authManager.currentUser.timeZone.toLocaleUpperCase())
        this.locationManager.getCurrentLocation().subscribe(location =>
        {
            this.partiesText3 = location.subsidiary.displayName + ' (the "Buyer"), ';
        });
    }

    async generateSignaturePage(parent: PurchaseOrderPDFComponent): Promise<void>
    {
        const vendor = parent.po.vendor;

        if(parent.po.isLocked){
            let loc = await lastValueFrom(this.locationManager.getCurrentLocation());
            this.partiesText3 = loc.subsidiary.displayName + ' (the "Buyer"), ';
            this.idNum = parent.po.contactLicenseNumber ?? 'N/A';
            this.idState = parent.po.contactState ?? 'N/A'
            
            this.selectedPaymentTypes = []
            if(parent.po.totalCash)
                this.selectedPaymentTypes.push(1);
            if(parent.po.totalCheck)
                this.selectedPaymentTypes.push(2);
            if(parent.po.totalWire)
                this.selectedPaymentTypes.push(4);
        }

        // Add Title
        parent.outputComplianceHeader(this.headerText);

        // ---------- SECTION 1 START ----------
        parent.pdf.setFontSize(parent.pdfDimensions.general.regularFontSize);
        parent.pdf.setFont('helvetica', null, 'normal');
        parent.outputTextLine(this.introRegularText1);
        parent.outputTextLine(this.introRegularText2);
        // ---------- END SECTION 1 ----------


        // ---------- SECTION 2 START ----------
        parent.result.bottomOfOutput += parent.lineHeight * 2;
        parent.outputTextLine(`${vendor.name} ${this.partiesText1} ${vendor.fullAddress},`);
        parent.result.bottomOfOutput += parent.lineHeight * 2;
        parent.outputTextLine(this.partiesText2);
        parent.result.bottomOfOutput += parent.lineHeight * 2;
        parent.outputTextLine(`${this.partiesText3} ${parent.po.location.fullAddress}`);
        parent.result.bottomOfOutput += parent.lineHeight * 2;
        parent.outputTextLine(this.partiesText4);
        // ---------- END SECTION 2 ----------

        // ---------- SECTION 3 START ----------
        // Use "table headers" to create the pseudo-table
        
        parent.createTableHeader(this.columns, true, false);;
        const columnOutput = cloneDeep(this.columns);
        columnOutput[0].title = parent.po.number;
        columnOutput[1].title = 'Converters';
        columnOutput[2].title = parent.po.totalNumConverters.toString();
        columnOutput[3].title = this.currencyPipe.transform(parent.po.totalConvertersPrice, parent.po.currency);
        columnOutput[4].title = this.currencyPipe.transform(parent.po.totalPrice, parent.po.currency);
        columnOutput[5].title = this.currencyPipe.transform(parent.po.catsAdjustment, parent.po.currency);
        columnOutput[6].title = this.currencyPipe.transform(parent.po.totalPrice + (parent.po.catsAdjustment ?? 0) + (parent.po.nonFerrousAdjustment ?? 0), parent.po.currency);
        parent.createTableRow(columnOutput, 0, true, false);
        columnOutput[0].title = parent.po.number;
        columnOutput[1].title = 'Non-Ferrous';
        columnOutput[2].title = parent.po.numMiscellaneous.toString();
        columnOutput[3].title = this.currencyPipe.transform(parent.po.totalMiscPrice, parent.po.currency, true);
        columnOutput[5].title = this.currencyPipe.transform(parent.po.nonFerrousAdjustment, parent.po.currency, true);
        parent.createTableRow(columnOutput, 1, true, false);
        parent.result.bottomOfOutput += parent.lineHeight;
        // ---------- END SECTION 3 ----------

        VehicleInfoComponent.addToPdf(parent);
        parent.result.bottomOfOutput += parent.lineHeight * 2;

        // tslint:disable:max-line-length

        // ---------- SECTION 4 START ----------
        // tslint:disable:no-bitwise
        parent.outputTextLine(this.paymentTypeHeaderText);
        let margin = 10;
        for (const paymentType of PaymentTypesSelectionComponent.paymentTypes)
        {
            parent.outputCheckbox(margin, this.selectedPaymentTypes.some(pt => pt === paymentType.value));
            if(paymentType.value == 1) {
                parent.result.bottomOfOutput += parent.lineHeight
                parent.outputText('Total: ' + this.currencyPipe.transform(this.poManager.current.totalCash, parent.po.currency), margin)
                parent.result.bottomOfOutput -= parent.lineHeight
            }
            if(paymentType.value == 2) {
                parent.result.bottomOfOutput += parent.lineHeight;
                parent.outputText('Total: ' + this.currencyPipe.transform(this.poManager.current.totalCheck, parent.po.currency), margin)
                parent.result.bottomOfOutput -= parent.lineHeight
                
                if(this.poManager.current.checkNumber != null && this.poManager.current.checkNumber != '') {
                    parent.result.bottomOfOutput += (parent.lineHeight * 2)
                    parent.outputText('Number: ' + this.poManager.current.checkNumber, margin)
                    parent.result.bottomOfOutput -= (parent.lineHeight * 2)
                }
            }
            if(paymentType.value == 4) {
                parent.result.bottomOfOutput += parent.lineHeight
                parent.outputText('Total: ' + this.currencyPipe.transform(this.poManager.current.totalWire, parent.po.currency), margin)
                parent.result.bottomOfOutput -= parent.lineHeight
            }
            margin += 15;
            parent.outputText(paymentType.title, margin);
            margin += parent.textWidth(paymentType.title) + 120;
        }
        // tslint:enable:no-bitwise
        // ---------- END SECTION 4 ----------

        // ---------- SECTION 5 START ----------
        parent.result.bottomOfOutput += parent.lineHeight * 3;
        parent.outputTextLine(this.sellerInfoHeaderText);
        parent.result.bottomOfOutput += parent.lineHeight * 1.1;
        parent.outputText(`${this.idText}  ${this.stateText}`);
        parent.outputText(this.idNum ?? 'N/A', parent.textWidth(this.removeUnderscores(this.idText) + 5));
        parent.outputTextLine(this.idState ?? 'N/A', parent.textWidth(`${this.idText}  `) +
        parent.textWidth(this.removeUnderscores(this.stateText) + 5));
        // ---------- END SECTION 5 ----------

        // ---------- SECTION 6 START ----------
        parent.result.bottomOfOutput += parent.lineHeight * 3;
        parent.outputTextLine(this.signaturePrelude1);
        parent.outputTextLine(this.signaturePrelude2);
        parent.result.bottomOfOutput += parent.lineHeight * 1.5;
        parent.pdf.setFontSize(9);
        parent.outputTextLine(this.signaturePrelude3);
        parent.pdf.setFontSize(parent.pdfDimensions.general.regularFontSize);

        parent.result.bottomOfOutput += parent.lineHeight * 1.5;

        const imageHeight = parent.lineHeight * 2;
        if(parent.po.isLocked){
            parent.outputImage(parent.po.signaturePageUrl, 'PNG', parent.leftMargin + parent.textWidth(this.removeUnderscores(this.signatureText)), imageHeight * 5, imageHeight);
        } else {
            const widthToHeightRatio = this.signature.widthToHeightRatio;
            parent.outputImage(this.signature.signature, 'PNG', parent.leftMargin + parent.textWidth(this.removeUnderscores(this.signatureText)), imageHeight * widthToHeightRatio, imageHeight);
        }
        
        parent.outputTextLine(this.signatureText);

        parent.result.bottomOfOutput += parent.lineHeight * 2;
        parent.outputText(`${this.printText}  ${this.dateText}`);
        parent.outputText(this.poManager.current.print, parent.textWidth(this.removeUnderscores(this.printText) + 5));
        parent.outputTextLine(moment(this.poManager.current.purchaseDate).format('MM-DD-yyyy'), parent.textWidth(`${this.printText}  `) +
            parent.textWidth(this.removeUnderscores(this.dateText) + 5));
        // ---------- END SECTION 6 ----------
        // tslint:enable:max-line-length

        this.clearFields();
    }

    get sellerSignatureFile(): File {
        return this.signature.getSignatureFile(`po_${this.poManager.current.id}_seller_signature.png`);
    }

    get partiesTextHTML(): string
    {
        const vendor = this.poManager.current.vendor;
        return `<div class="party-line">${vendor.name} ${this.partiesText1} ${vendor.fullAddress},</div>` +
            `<div>${this.partiesText2}</div>` + `<div>${this.partiesText3} ${this.poManager.current.location.fullAddress}</div>` + `<div>${this.partiesText4}</div>`;
    }

    get signaturePrelude(): string
    {
        return `${this.signaturePrelude1} ${this.signaturePrelude2}`;
    }

    get signaturePreludeWarning(): string
    {
        return `${this.signaturePrelude3}`;
    }

    removeUnderscores(text: string): string
    {
        return text.replace(/_/g, '');
    }

    clearSignatures(): void
    {
        this.signature?.clear();
    }

    onSelectedContactChanged(event: any): void
    {
        this.selectedContact = event.value;
        this.contactValid = this.selectedContact.isContactValidForPO;
        // if (!this.selectedContact.isContactValidForPO) {
        //     this.messageService.add({ severity: 'error', summary: 'Invalid Contact', detail: `Contact has invalid driver's license information and cannot be selected.` });
        //     setTimeout(() => {
        //         this.idNum = '';
        //         this.idState = '';
        //         this.poManager.current.print = '';
        //         this.selectedContact = undefined;
        //     }, 100);
        //     return;
        // }
        this.idNum = this.selectedContact.driversLicenseNumber ?? '';
        this.idState = this.selectedContact.driversLicenseState ?? '';
        this.poManager.current.print = this.selectedContact.fullName;
    }

    onCancel(): void
    {
        VehicleInfoComponent.clearInfo();
        this.clearFields();
        this.signatureCompleted.emit(false);
    }

    onSave(): void
    {
        this.poManager.current.timeZone = this.selectedTimezone.fullName;
        this.poManager.current.paymentType =
            // tslint:disable-next-line:no-bitwise
            (this.selectedPaymentTypes.includes(PurchaseOrderPaymentType.Cash) ? PurchaseOrderPaymentType.Cash : 0) |
            (this.selectedPaymentTypes.includes(PurchaseOrderPaymentType.Check) ? PurchaseOrderPaymentType.Check : 0) |
            (this.selectedPaymentTypes.includes(PurchaseOrderPaymentType.WireTransfer) ? PurchaseOrderPaymentType.WireTransfer : 0);
        this.signatureCompleted.emit(true);
    }

    private clearFields(): void
    {
        this.idNum = '';
        this.idState = '';
        this.selectedPaymentTypes = [];
        // this.selectedContact = undefined;
        this.selectedPaymentTypes = []
        // this.clearSignatures();
    }
}
